import keys from 'constants/keys'

/**
 * ── Task-level status model (single source of truth) ─────────────────────────
 *
 * A task/context has exactly FIVE statuses. Everything else that used to be a task
 * status (approved-with-reservation, AI-reviewed) is a *subtask* nuance: it folds into
 * `approved` at the task level and is surfaced separately as a per-subtask breakdown
 * ("2/4 approved · 2/4 approved w/ reservation").
 *
 * Derivation rules (`deriveTaskStatus`):
 *   1. nothing submitted                      → todo
 *   2. at least one rejected subtask          → rejected
 *   3. not every subtask reached a terminal    → inProgress
 *      status
 *   4. every subtask is in the approval        → approved
 *      family {APPROVED, APPROVED_WITH_RESERVATION, REVIEWED_BY_AI}
 *      (SKIPPED is neutral — never sent for audit, so it does not block `approved`)
 *   5. otherwise (mix of done + approvals)     → done
 *
 * This module intentionally depends ONLY on `constants/keys` so both `utils/taskAnalytics`
 * and UI components can import it without a cycle. Colors live in `TASK_STATUS_CONFIG`
 * (`utils/taskAnalytics`) — its keys match `SubtaskStatusKey` 1:1, so a consumer that needs
 * a color can index it directly with a breakdown key.
 */

export type TaskStatusId = 'todo' | 'inProgress' | 'done' | 'approved' | 'rejected'

/** Per-subtask status buckets — the granularity used for the "why this status" breakdown. */
export type SubtaskStatusKey =
  | 'todo'
  | 'inProgress'
  | 'done'
  | 'skipped'
  | 'approved'
  | 'approvedWithReservation'
  | 'reviewedByAi'
  | 'rejected'

export type SubtaskStatusCounts = Record<SubtaskStatusKey, number> & {
  /** Total subtasks in the task (denominator of the breakdown). */
  total: number
  /** Subtasks that reached a terminal status (done/skipped/approved/·w-reservation/AI/rejected). */
  terminal: number
}

export const TASK_STATUS_VALUE_BY_ID: Record<TaskStatusId, number> = {
  todo: keys.TASK_STATUSES.TODO.value,
  inProgress: keys.TASK_STATUSES.INPROGRESS.value,
  done: keys.TASK_STATUSES.DONE.value,
  approved: keys.TASK_STATUSES.APPROVED.value,
  rejected: keys.TASK_STATUSES.DISAPPROVED.value,
}

/**
 * Numeric status → one of the 5 task statuses. Used when only an aggregate number is
 * available (legacy V1 tasks, a server-provided context aggregate) and the per-subtask
 * statuses cannot be recounted. SKIPPED folds into done; 5 and -5 fold into approved.
 */
export const TASK_STATUS_ID_BY_VALUE: Partial<Record<number, TaskStatusId>> = {
  [keys.TASK_STATUSES.TODO.value]: 'todo',
  [keys.TASK_STATUSES.INPROGRESS.value]: 'inProgress',
  [keys.TASK_STATUSES.DONE.value]: 'done',
  [keys.TASK_STATUSES.SKIPPED.value]: 'done',
  [keys.TASK_STATUSES.APPROVED.value]: 'approved',
  [keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.value]: 'approved',
  [keys.TASK_STATUSES.REVIEWED_BY_AI.value]: 'approved',
  [keys.TASK_STATUSES.DISAPPROVED.value]: 'rejected',
}

/** Collapses any raw status number to the 5-status set. */
export const normalizeTaskStatusValue = (status: number): number =>
  TASK_STATUS_VALUE_BY_ID[TASK_STATUS_ID_BY_VALUE[status] ?? 'todo']

export const SUBTASK_STATUS_LOCALE_KEY: Record<SubtaskStatusKey, string> = {
  todo: keys.TASK_STATUSES.TODO.localeLiteral,
  inProgress: keys.TASK_STATUSES.INPROGRESS.localeLiteral,
  done: keys.TASK_STATUSES.DONE.localeLiteral,
  skipped: keys.TASK_STATUSES.SKIPPED.localeLiteral,
  approved: keys.TASK_STATUSES.APPROVED.localeLiteral,
  approvedWithReservation: keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.localeLiteral,
  reviewedByAi: keys.TASK_STATUSES.REVIEWED_BY_AI.localeLiteral,
  rejected: keys.TASK_STATUSES.DISAPPROVED.localeLiteral,
}

export const TASK_STATUS_LOCALE_KEY: Record<TaskStatusId, string> = {
  todo: keys.TASK_STATUSES.TODO.localeLiteral,
  inProgress: keys.TASK_STATUSES.INPROGRESS.localeLiteral,
  done: keys.TASK_STATUSES.DONE.localeLiteral,
  approved: keys.TASK_STATUSES.APPROVED.localeLiteral,
  rejected: keys.TASK_STATUSES.DISAPPROVED.localeLiteral,
}

/**
 * Display order of the status breakdown — a FIXED semantic order, never sorted by size, so that
 * rows in a list stay visually comparable. `rejected` comes first: it is the most consequential
 * outcome and must never hide at the end of the bar.
 */
export const SUBTASK_STATUS_ORDER: SubtaskStatusKey[] = [
  'rejected',
  'done',
  'approved',
  'approvedWithReservation',
  'reviewedByAi',
  'skipped',
  'inProgress',
  'todo',
]

/**
 * Task statuses that mean "the work is over". Only these get a status-composition breakdown: for
 * `inProgress` / `todo` the row already has a progress bar answering the same question.
 */
const TERMINAL_TASK_STATUSES: TaskStatusId[] = ['done', 'approved', 'rejected']

export const isTerminalTaskStatus = (status: TaskStatusId): boolean => TERMINAL_TASK_STATUSES.indexOf(status) !== -1

/** Statuses that mean "the auditor (or AI) signed this subtask off". */
const APPROVAL_FAMILY: SubtaskStatusKey[] = ['approved', 'approvedWithReservation', 'reviewedByAi']

const EMPTY_COUNTS = (): SubtaskStatusCounts => ({
  todo: 0,
  inProgress: 0,
  done: 0,
  skipped: 0,
  approved: 0,
  approvedWithReservation: 0,
  reviewedByAi: 0,
  rejected: 0,
  total: 0,
  terminal: 0,
})

const bucketOf = (status: number): SubtaskStatusKey | null => {
  switch (status) {
    case keys.TASK_STATUSES.TODO.value:
      return 'todo'
    case keys.TASK_STATUSES.INPROGRESS.value:
      return 'inProgress'
    // -1 is the legacy V1 "submitted" status — treat it as done so counts stay in sync.
    case keys.TASK_STATUSES.DONE.value:
    case -1:
      return 'done'
    case keys.TASK_STATUSES.SKIPPED.value:
      return 'skipped'
    case keys.TASK_STATUSES.APPROVED.value:
      return 'approved'
    case keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.value:
      return 'approvedWithReservation'
    case keys.TASK_STATUSES.REVIEWED_BY_AI.value:
      return 'reviewedByAi'
    case keys.TASK_STATUSES.DISAPPROVED.value:
      return 'rejected'
    default:
      return null
  }
}

const TERMINAL_KEYS: SubtaskStatusKey[] = [
  'done',
  'skipped',
  'approved',
  'approvedWithReservation',
  'reviewedByAi',
  'rejected',
]

/**
 * Counts subtask result statuses into buckets.
 *
 * @param statuses    result statuses of the subtasks that HAVE a result
 * @param subtaskTotal total number of subtasks in the task. Subtasks without a result are
 *                     counted as `todo`. Falls back to `statuses.length` when unknown.
 */
export const countSubtaskStatuses = (statuses: number[], subtaskTotal?: number): SubtaskStatusCounts => {
  const counts = EMPTY_COUNTS()

  statuses.forEach((status) => {
    const bucket = bucketOf(status)
    if (bucket) counts[bucket] += 1
  })

  // A subtask with no result row at all is "not started".
  const total = Math.max(subtaskTotal ?? statuses.length, statuses.length)
  counts.todo += Math.max(0, total - statuses.length)
  counts.total = total
  counts.terminal = TERMINAL_KEYS.reduce((sum, key) => sum + counts[key], 0)

  return counts
}

/** The 5-status rule. See the module docblock for the ordered conditions. */
export const deriveTaskStatus = (counts: SubtaskStatusCounts): TaskStatusId => {
  if (counts.rejected > 0) return 'rejected'
  if (counts.terminal === 0 && counts.inProgress === 0) return 'todo'
  if (counts.terminal < counts.total) return 'inProgress'

  const approvalCount = APPROVAL_FAMILY.reduce((sum, key) => sum + counts[key], 0)
  // Every subtask is either signed off or skipped (and at least one was actually signed off).
  if (approvalCount > 0 && approvalCount + counts.skipped === counts.total) return 'approved'

  return 'done'
}

/** Same as `deriveTaskStatus` but returns the numeric `keys.TASK_STATUSES` value. */
export const deriveTaskStatusValue = (counts: SubtaskStatusCounts): number =>
  TASK_STATUS_VALUE_BY_ID[deriveTaskStatus(counts)]

/**
 * The "why is this task `done`?" explanation: non-zero subtask buckets in `SUBTASK_STATUS_ORDER`.
 * Returns `[]` when there is nothing to explain.
 *
 * With `taskStatus` given, a single bucket is dropped only when it repeats the badge (4/4 approved
 * under "Approved" says nothing new). A single bucket that is NAMED DIFFERENTLY from the status is the
 * whole answer and is kept: 4/4 reviewed-by-AI or 4/4 approved-with-reservation both read as
 * "Approved", 4/4 skipped reads as "Done". Without `taskStatus` any single bucket is dropped
 * (previous behaviour).
 */
export const getSubtaskStatusBreakdown = (
  counts: SubtaskStatusCounts,
  taskStatus?: TaskStatusId
): Array<{ key: SubtaskStatusKey; count: number }> => {
  const items = SUBTASK_STATUS_ORDER.filter((key) => counts[key] > 0).map((key) => ({ key, count: counts[key] }))

  if (items.length > 1) return items
  // `SubtaskStatusKey` and `TaskStatusId` share their names for todo/inProgress/done/approved/rejected,
  // so a plain comparison answers "does this bucket just repeat the badge?".
  if (items.length === 1 && taskStatus && (items[0].key as string) !== (taskStatus as string)) return items

  return []
}
